import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import axios from 'axios';
import {
  Trophy, AlertTriangle, ArrowRight, Calculator, Layers, Search,
} from 'lucide-react';
import BackOrHomeButton from '@/components/BackOrHomeButton';
import SiteDisclaimer from '@/components/SiteDisclaimer';
import { useSeoMeta } from '@/pages/learn/LearnPage';
import { fmtPct, fmtMoney, fmtAum, fmtDate } from '@/components/coveredCall/ccFormat';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SLUG_RE = /^[a-z]{1,6}-vs-[a-z]{1,6}$/i;

/**
 * "[A] vs [B]" covered-call head-to-head — route /:pair (e.g. /msty-vs-ymax)
 *
 * Registered as a single-segment dynamic route, so anything that isn't
 * a `x-vs-y` slug bounces to the root. Data is read-only from
 * stock_cache via /api/covered-call/compare/:slug.
 */
export default function CoveredCallVersus() {
  const { pair } = useParams();
  const valid = SLUG_RE.test(pair || '');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!valid) return;
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    axios.get(`${API}/covered-call/compare/${pair.toLowerCase()}`)
      .then((r) => { if (!cancelled) { setData(r.data); setLoading(false); } })
      .catch(() => { if (!cancelled) { setNotFound(true); setLoading(false); } });
    return () => { cancelled = true; };
  }, [pair, valid]);

  const [aT, bT] = useMemo(() => {
    const m = SLUG_RE.test(pair || '') ? pair.split('-vs-') : ['', ''];
    return [m[0].toUpperCase(), m[1].toUpperCase()];
  }, [pair]);

  const a = data?.a;
  const b = data?.b;
  const cmp = data?.comparison;
  const winner = cmp?.overall_winner === 'a' ? a : cmp?.overall_winner === 'b' ? b : null;
  const loser = cmp?.overall_winner === 'a' ? b : cmp?.overall_winner === 'b' ? a : null;

  useSeoMeta({
    title: data
      ? `${aT} vs ${bT}: yield, fees and true total return compared | DiviDrip`
      : `${aT} vs ${bT} | DiviDrip`,
    description: data
      ? `${aT} yields ${fmtPct(a.dividend_yield)} and ${bT} yields ${fmtPct(b.dividend_yield)}, but over 12 months ${aT} returned ${fmtPct(a.total_return_1y, 1, true)} vs ${fmtPct(b.total_return_1y, 1, true)} for ${bT}. Full distribution history, fees and NAV erosion.`
      : `Compare ${aT} and ${bT} — distribution yield, expense ratio, NAV erosion and true 1-year total return.`,
    canonical: `https://twylightcrow.com/${(data?.canonical_slug || pair || '').toLowerCase()}`,
  });

  useEffect(() => {
    if (!data) return;
    const faqs = buildFaqs(a, b, cmp);
    const jsonLd = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    };
    const s = document.createElement('script');
    s.type = 'application/ld+json';
    s.text = JSON.stringify(jsonLd);
    s.setAttribute('data-testid', 'cc-vs-jsonld');
    document.head.appendChild(s);
    return () => { s.remove(); };
  }, [data, a, b, cmp]);

  if (!valid) return <Navigate to="/" replace />;

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto px-4 py-12 animate-pulse space-y-4" data-testid="cc-vs-loading">
        <div className="h-10 w-96 bg-slate-200 dark:bg-slate-800 rounded" />
        <div className="h-40 bg-slate-200 dark:bg-slate-800 rounded-xl" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-12" data-testid="cc-vs-notfound">
        <BackOrHomeButton />
        <h1 className="mt-6 text-3xl font-black text-slate-900 dark:text-slate-100">
          We don't have both {aT} and {bT} yet
        </h1>
        <p className="mt-3 text-slate-600 dark:text-slate-400">
          One of these tickers isn't in our database, so we can't run an honest comparison.
          Browse every option-income ETF we do track instead.
        </p>
        <Link to="/covered-call-etfs" className="mt-6 inline-flex items-center gap-2 px-4 py-2 bg-cyan-600 hover:bg-cyan-500 text-white font-semibold rounded-lg" data-testid="cc-vs-notfound-hub">
          <Layers className="w-4 h-4" /> Covered call ETF list
        </Link>
        <SiteDisclaimer />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-8" data-testid="cc-vs-page">
      <BackOrHomeButton />

      <div className="mt-4 flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-cyan-600 dark:text-cyan-400 font-semibold">
        <Layers className="w-3.5 h-3.5" />
        <Link to="/covered-call-etfs" className="hover:underline" data-testid="cc-vs-breadcrumb">Option income research</Link>
      </div>

      <h1 className="mt-2 text-4xl sm:text-5xl font-black tracking-tight text-slate-900 dark:text-slate-100">
        <span className="font-mono">{a.ticker}</span> vs <span className="font-mono">{b.ticker}</span>
      </h1>
      <p className="mt-3 text-base text-slate-600 dark:text-slate-400 max-w-3xl">
        {a.name} against {b.name}. Headline yields tell you almost nothing here —
        below is what each fund actually distributed, what happened to the share price, and the
        total return that combination produced.
      </p>

      {/* Verdict */}
      <div className="mt-8 p-6 rounded-2xl border-2 border-cyan-300 dark:border-cyan-800 bg-gradient-to-br from-cyan-50 to-emerald-50/60 dark:from-slate-900 dark:to-slate-900/40" data-testid="cc-vs-verdict">
        <div className="flex items-center gap-2 text-xs uppercase tracking-wider font-bold text-cyan-700 dark:text-cyan-400">
          <Trophy className="w-4 h-4" /> 12-month scoreboard
        </div>
        {winner ? (
          <div className="mt-3 flex flex-wrap items-baseline gap-x-4 gap-y-1">
            <div className="text-3xl font-black text-slate-900 dark:text-slate-100" data-testid="cc-vs-winner">
              {winner.total_return_1y != null && winner.total_return_1y < 0
                ? `${winner.ticker} lost less`
                : `${winner.ticker} won`}
            </div>
            <div className={`text-lg font-bold tabular-nums ${winner.total_return_1y != null && winner.total_return_1y < 0 ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400'}`}>
              {fmtPct(winner.total_return_1y, 1, true)}
            </div>
            <div className="text-sm text-slate-500 dark:text-slate-400">
              vs {fmtPct(loser?.total_return_1y, 1, true)} for {loser?.ticker}
            </div>
          </div>
        ) : (
          <div className="mt-3 text-2xl font-black text-slate-900 dark:text-slate-100" data-testid="cc-vs-winner">
            Not enough history for a verdict
          </div>
        )}
        <p className="mt-3 text-sm text-slate-700 dark:text-slate-300 leading-relaxed max-w-3xl">{cmp.verdict}</p>
        {(a.as_of || b.as_of) && (
          <p className="mt-2 text-xs text-slate-500 dark:text-slate-400" data-testid="cc-vs-asof">
            Prices and 1-year performance as of {fmtDate(a.as_of || b.as_of)}; distributions through{' '}
            {fmtDate((a.history?.slice(-1)[0] || {}).date || a.last_dividend_date)}.
          </p>
        )}

        {!!cmp.flags?.length && (
          <div className="mt-4 flex flex-col gap-2" data-testid="cc-vs-flags">
            {cmp.flags.map((f, i) => (
              <div key={i} className="flex items-start gap-2 text-xs text-amber-800 dark:text-amber-300" data-testid={`cc-vs-flag-${i}`}>
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <span>{f.text}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Total return math */}
      <section className="mt-10 grid md:grid-cols-2 gap-4" data-testid="cc-vs-math">
        {[a, b].map((f) => (
          <MathCard key={f.ticker} fund={f} />
        ))}
      </section>

      {/* Metric table */}
      <section className="mt-10" data-testid="cc-vs-metrics">
        <h2 className="text-lg md:text-lg font-bold text-slate-900 dark:text-slate-100">Side by side</h2>
        <div className="mt-3 overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-800">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 dark:bg-slate-900/60">
              <tr>
                <th className="px-4 py-2.5 text-left text-[10px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400">Metric</th>
                <th className="px-4 py-2.5 text-right text-xs font-mono font-bold text-slate-900 dark:text-slate-100">{a.ticker}</th>
                <th className="px-4 py-2.5 text-right text-xs font-mono font-bold text-slate-900 dark:text-slate-100">{b.ticker}</th>
              </tr>
            </thead>
            <tbody>
              <MetricRow label="1-year total return" a={fmtPct(a.total_return_1y, 1, true)} b={fmtPct(b.total_return_1y, 1, true)} win={cmp.winners.total_return_1y} badge={winner && winner.total_return_1y < 0 ? 'less bad' : 'win'} emphasis testId="tr" />
              <MetricRow label="Headline distribution yield" a={fmtPct(a.dividend_yield)} b={fmtPct(b.dividend_yield)} win={cmp.winners.dividend_yield} badge="higher" testId="yield" />
              <MetricRow label="Paid per share (12mo)" a={fmtMoney(a.dist_ttm)} b={fmtMoney(b.dist_ttm)} win={cmp.winners.dist_ttm} badge="higher" testId="dist" />
              <MetricRow label="Share price change (1y)" a={fmtPct(a.nav_change_1y, 1, true)} b={fmtPct(b.nav_change_1y, 1, true)} win={cmp.winners.nav_change_1y} badge="better" testId="nav" />
              <MetricRow label="Payout trend (last 3 vs prior 3)" a={fmtPct(a.dist_trend_pct, 1, true)} b={fmtPct(b.dist_trend_pct, 1, true)} win={cmp.winners.dist_trend_pct} badge="better" testId="trend" />
              <MetricRow label="Expense ratio" a={fmtPct(a.expense_ratio, 2)} b={fmtPct(b.expense_ratio, 2)} win={cmp.winners.expense_ratio} badge="cheaper" testId="fee" />
              <MetricRow label="Distribution frequency" a={a.frequency || '—'} b={b.frequency || '—'} testId="freq" />
              <MetricRow label="Payouts in last 12mo" a={a.payouts_ttm ?? '—'} b={b.payouts_ttm ?? '—'} testId="payouts" />
              <MetricRow label="Fund size" a={fmtAum(a.net_assets)} b={fmtAum(b.net_assets)} win={cmp.winners.net_assets} badge="bigger" testId="aum" />
              <MetricRow label="Share price" a={fmtMoney(a.price)} b={fmtMoney(b.price)} testId="price" />
              <MetricRow label="Next ex-distribution" a={a.next_ex_date || 'Not declared'} b={b.next_ex_date || 'Not declared'} testId="nextex" />
              <MetricRow label="Last distribution" a={a.last_dividend_value != null ? `${fmtMoney(a.last_dividend_value)} · ${fmtDate(a.last_dividend_date)}` : '—'} b={b.last_dividend_value != null ? `${fmtMoney(b.last_dividend_value)} · ${fmtDate(b.last_dividend_date)}` : '—'} testId="lastdist" />
              <MetricRow label="Fund family" a={a.family} b={b.family} testId="family" />
            </tbody>
          </table>
        </div>
      </section>

      {/* Distribution history */}
      <section className="mt-10" data-testid="cc-vs-history">
        <h2 className="text-lg md:text-lg font-bold text-slate-900 dark:text-slate-100">Distribution history</h2>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          Last 12 declared payouts per share. Shrinking bars mean the option premium isn't holding up.
        </p>
        <div className="mt-4 grid md:grid-cols-2 gap-4">
          {[a, b].map((f) => <HistoryCard key={f.ticker} fund={f} />)}
        </div>
      </section>

      {/* Which one for whom */}
      <section className="mt-10 grid md:grid-cols-2 gap-4" data-testid="cc-vs-suitability">
        {[a, b].map((f) => (
          <div key={f.ticker} className="p-5 rounded-xl border border-slate-200 dark:border-slate-800">
            <div className="font-mono font-bold text-slate-900 dark:text-slate-100">{f.ticker} suits you if…</div>
            <ul className="mt-2 space-y-1.5 text-sm text-slate-600 dark:text-slate-400">
              {suitabilityBullets(f).map((t, i) => (
                <li key={i} className="flex gap-2"><span className="text-cyan-500">•</span><span>{t}</span></li>
              ))}
            </ul>
          </div>
        ))}
      </section>

      {/* Related pairs */}
      {!!data.related_pairs?.length && (
        <section className="mt-10" data-testid="cc-vs-related">
          <h2 className="text-lg md:text-lg font-bold text-slate-900 dark:text-slate-100">Other comparisons</h2>
          <div className="mt-3 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {data.related_pairs.map((p) => (
              <Link
                key={p.slug}
                to={`/${p.slug}`}
                className="group flex items-center justify-between gap-3 px-4 py-3 rounded-lg border border-slate-200 dark:border-slate-800 hover:border-cyan-400 dark:hover:border-cyan-600 transition-colors"
                data-testid={`cc-vs-related-${p.slug}`}
              >
                <span className="font-mono font-bold text-slate-900 dark:text-slate-100">
                  {p.a} <span className="text-slate-400 font-sans font-normal text-xs px-1">vs</span> {p.b}
                </span>
                <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-cyan-500 transition-transform group-hover:translate-x-0.5" />
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="mt-10 p-6 rounded-xl bg-gradient-to-br from-cyan-50 to-emerald-50 dark:from-slate-900 dark:to-slate-900/60 border border-slate-200 dark:border-slate-800" data-testid="cc-vs-cta">
        <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100 mb-3">Next step</h2>
        <div className="flex flex-wrap gap-3">
          <Link to="/covered-call-etfs" className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-600 hover:bg-cyan-500 text-white font-semibold rounded-lg" data-testid="cc-vs-cta-hub">
            <Layers className="w-4 h-4" /> All covered call ETFs
          </Link>
          <Link to="/dividend-reinvestment-calculator" className="inline-flex items-center gap-2 px-4 py-2 border border-slate-300 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg" data-testid="cc-vs-cta-calc">
            <Calculator className="w-4 h-4" /> Model the income
          </Link>
          <Link to="/screener" className="inline-flex items-center gap-2 px-4 py-2 border border-slate-300 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg" data-testid="cc-vs-cta-screener">
            <Search className="w-4 h-4" /> Screener
          </Link>
        </div>
      </section>

      <VersusFaq a={a} b={b} cmp={cmp} />

      <SiteDisclaimer />
    </div>
  );
}

/* ─── Sub-components ────────────────────────────────────────────── */

function MathCard({ fund }) {
  const good = fund.total_return_1y != null && fund.total_return_1y >= 0;
  return (
    <div className="p-5 rounded-xl border border-slate-200 dark:border-slate-800" data-testid={`cc-vs-math-${fund.ticker}`}>
      <div className="flex items-baseline justify-between gap-2">
        <div className="font-mono text-lg font-black text-slate-900 dark:text-slate-100">{fund.ticker}</div>
        <div className="text-xs text-slate-500 dark:text-slate-400">{fund.family}</div>
      </div>
      <dl className="mt-3 space-y-2 text-sm">
        <MathRow label="Distributions received (12mo)" value={fund.dist_yield_on_cost_1y != null ? fmtPct(fund.dist_yield_on_cost_1y, 1, true) : '—'} tone="pos" />
        <MathRow label="Share price change" value={fmtPct(fund.nav_change_1y, 1, true)} tone={fund.nav_change_1y != null && fund.nav_change_1y < 0 ? 'neg' : 'pos'} />
        <div className="pt-2 border-t border-slate-200 dark:border-slate-800 flex items-center justify-between">
          <dt className="font-bold text-slate-900 dark:text-slate-100">Total return</dt>
          <dd className={`text-xl font-black tabular-nums ${fund.total_return_1y == null ? 'text-slate-400' : good ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
            {fmtPct(fund.total_return_1y, 1, true)}
          </dd>
        </div>
      </dl>
      <p className="mt-3 text-xs text-slate-500 dark:text-slate-400">
        {fund.dist_ttm != null
          ? `${fmtMoney(fund.dist_ttm)} paid per share across ${fund.payouts_ttm || '—'} distributions.`
          : 'Distribution history is still building for this fund.'}
      </p>
    </div>
  );
}

function MathRow({ label, value, tone }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <dt className="text-slate-600 dark:text-slate-400">{label}</dt>
      <dd className={`font-semibold tabular-nums ${tone === 'neg' ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400'}`}>{value}</dd>
    </div>
  );
}

function MetricRow({ label, a, b, win, badge = 'win', emphasis, testId }) {
  const cell = (side, value) => {
    const isWin = win === side;
    return (
      <td className={`px-4 py-2.5 text-right tabular-nums whitespace-nowrap ${isWin ? 'font-bold text-slate-900 dark:text-slate-100' : 'text-slate-700 dark:text-slate-300'}`}>
        {value}{isWin && <span className="ml-1.5 text-[10px] uppercase tracking-wider text-cyan-600 dark:text-cyan-400">{badge}</span>}
      </td>
    );
  };
  return (
    <tr className={`border-t border-slate-100 dark:border-slate-800/70 ${emphasis ? 'bg-slate-50/80 dark:bg-slate-900/40' : ''}`} data-testid={`cc-vs-metric-${testId}`}>
      <td className={`px-4 py-2.5 text-left ${emphasis ? 'font-bold text-slate-900 dark:text-slate-100' : 'text-slate-600 dark:text-slate-400'}`}>{label}</td>
      {cell('a', a)}
      {cell('b', b)}
    </tr>
  );
}

function HistoryCard({ fund }) {
  const rows = (fund.history || []).slice(-12);
  const max = rows.reduce((m, r) => Math.max(m, r.amount), 0) || 1;
  return (
    <div className="p-5 rounded-xl border border-slate-200 dark:border-slate-800" data-testid={`cc-vs-history-${fund.ticker}`}>
      <div className="flex items-baseline justify-between">
        <div className="font-mono font-bold text-slate-900 dark:text-slate-100">{fund.ticker}</div>
        <div className="text-xs text-slate-500 dark:text-slate-400">{fund.frequency || ''}</div>
      </div>
      {rows.length ? (
        <>
          <div className="mt-4 flex items-end gap-1.5 h-28">
            {rows.map((r) => (
              <div key={r.date} className="flex-1 group relative flex flex-col justify-end h-full" title={`${r.date}: ${fmtMoney(r.amount, 4)}`}>
                <div
                  className="w-full rounded-t bg-cyan-500/80 group-hover:bg-cyan-400 transition-colors"
                  style={{ height: `${Math.max(4, (r.amount / max) * 100)}%` }}
                />
              </div>
            ))}
          </div>
          <div className="mt-2 flex justify-between text-[10px] text-slate-500 dark:text-slate-400">
            <span>{fmtDate(rows[0].date)}</span>
            <span>{fmtDate(rows[rows.length - 1].date)}</span>
          </div>
        </>
      ) : (
        <p className="mt-4 text-sm text-slate-500 dark:text-slate-400">No distribution history cached yet.</p>
      )}
    </div>
  );
}

function suitabilityBullets(f) {
  const out = [];
  if (f.dividend_yield != null && f.dividend_yield >= 40) {
    out.push(`You want maximum cash flow (${fmtPct(f.dividend_yield)} headline) and accept that the share price will likely grind lower.`);
  } else if (f.dividend_yield != null) {
    out.push(`You want a more moderate ${fmtPct(f.dividend_yield)} option-income stream without giving up most of the underlying growth.`);
  }
  if (f.total_return_1y != null && f.total_return_1y >= 0) {
    out.push(`Its last 12 months were net positive (${fmtPct(f.total_return_1y, 1, true)}) after distributions.`);
  } else if (f.total_return_1y != null) {
    out.push(`You are comfortable with a negative 12-month total return (${fmtPct(f.total_return_1y, 1, true)}) in exchange for the income stream.`);
  }
  if (f.expense_ratio != null) {
    out.push(f.expense_ratio >= 1
      ? `You accept a ${fmtPct(f.expense_ratio, 2)} annual fee for the single-stock options strategy.`
      : `You prefer a lower ${fmtPct(f.expense_ratio, 2)} annual fee.`);
  }
  if (f.frequency) out.push(`${f.frequency} distributions fit how you draw income.`);
  if (f.net_assets != null) out.push(`Fund size of ${fmtAum(f.net_assets)} matters to you for liquidity.`);
  return out.slice(0, 5);
}

function buildFaqs(a, b, cmp) {
  const wt = cmp?.overall_winner === 'a' ? a?.ticker : cmp?.overall_winner === 'b' ? b?.ticker : null;
  return [
    {
      q: `Is ${a.ticker} or ${b.ticker} the better investment?`,
      a: wt
        ? `Measured on 12-month total return — share-price change plus every distribution paid — ${wt} came out ahead: ${fmtPct((wt === a.ticker ? a : b).total_return_1y, 1, true)} versus ${fmtPct((wt === a.ticker ? b : a).total_return_1y, 1, true)}. Past total return is not a forecast, but it is a far better guide than headline yield.`
        : `We don't yet have a full 12 months of price and distribution history for both funds, so neither can be declared the winner on total return. Compare the distribution history and expense ratios above.`,
    },
    {
      q: `What is the yield of ${a.ticker} vs ${b.ticker}?`,
      a: `${a.ticker} currently shows a distribution yield of ${fmtPct(a.dividend_yield)} and ${b.ticker} shows ${fmtPct(b.dividend_yield)}. Over the trailing 12 months ${a.ticker} actually paid ${fmtMoney(a.dist_ttm)} per share and ${b.ticker} paid ${fmtMoney(b.dist_ttm)} per share.`,
    },
    {
      q: `Does ${a.ticker} lose value over time?`,
      a: a.nav_change_1y != null
        ? `${a.ticker}'s share price changed ${fmtPct(a.nav_change_1y, 1, true)} over the last 12 months. ${a.nav_change_1y < 0 ? 'That decline means part of the distribution was funded from capital rather than option premium.' : 'The price held up, so the distributions were not being funded by NAV erosion over this period.'}`
        : `We don't have a full year of price history for ${a.ticker} yet.`,
    },
    {
      q: `Which fund charges more, ${a.ticker} or ${b.ticker}?`,
      a: `${a.ticker} charges ${fmtPct(a.expense_ratio, 2)} a year and ${b.ticker} charges ${fmtPct(b.expense_ratio, 2)}. Fees come straight out of the option premium that funds your distribution.`,
    },
    {
      q: `How often do ${a.ticker} and ${b.ticker} pay?`,
      a: `${a.ticker} pays ${(a.frequency || 'on its published schedule').toLowerCase()} (${a.payouts_ttm || '—'} distributions in the last 12 months) and ${b.ticker} pays ${(b.frequency || 'on its published schedule').toLowerCase()} (${b.payouts_ttm || '—'} distributions). Paying more often does not increase total return — it just splits the same amount into smaller cheques.`,
    },
  ];
}

function VersusFaq({ a, b, cmp }) {
  const faqs = buildFaqs(a, b, cmp);
  return (
    <section className="mt-12" data-testid="cc-vs-faq">
      <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100 mb-3">Frequently asked</h2>
      <div className="space-y-3">
        {faqs.map((f, i) => (
          <details key={i} className="p-4 rounded-lg border border-slate-200 dark:border-slate-800 group" data-testid={`cc-vs-faq-item-${i}`}>
            <summary className="cursor-pointer font-semibold text-slate-900 dark:text-slate-100 list-none flex items-start justify-between gap-3">
              <span>{f.q}</span>
              <ArrowRight className="w-4 h-4 mt-1 shrink-0 text-slate-400 group-open:rotate-90 transition-transform" />
            </summary>
            <p className="mt-3 text-sm text-slate-700 dark:text-slate-300 leading-relaxed">{f.a}</p>
          </details>
        ))}
      </div>
    </section>
  );
}
